import { useState } from 'react';
import { Button } from '../ui/Button';
import { Toggle } from '../ui/Toggle';
import { trpc } from '../../lib/trpc';
import { FormSection } from '../ui/FormSection';

interface ProfileFieldProps {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
}

const ProfileField = ({ label, value, placeholder }: ProfileFieldProps) => (
  <div className="flex items-start justify-between gap-4">
    <dt className="text-sm text-gray-500 flex-shrink-0">{label}</dt>
    {value ? (
      <dd className="text-sm text-gray-900 text-right">{value}</dd>
    ) : (
      <dd className="text-sm text-gray-400 italic text-right">{placeholder || 'Not set'}</dd>
    )}
  </div>
);

interface ProfileData {
  name: string | null;
  bio: string | null;
  birthday: string | null;
  homeLocation: { city?: string; state?: string; country?: string } | null;
  address: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  } | null;
  publicProfile: boolean;
  privacySettings: { fullName: boolean; checkIns: boolean };
}

interface ProfileTabProps {
  profile: ProfileData;
  email: string;
}

export const ProfileTab = ({ profile, email }: ProfileTabProps) => {
  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [name, setName] = useState(profile.name || '');
  const [bio, setBio] = useState(profile.bio || '');
  const [birthday, setBirthday] = useState(
    profile.birthday ? profile.birthday.split('T')[0] : ''
  );
  const [city, setCity] = useState(profile.homeLocation?.city || '');
  const [state, setState] = useState(profile.homeLocation?.state || '');
  const [country, setCountry] = useState(profile.homeLocation?.country || '');
  const [publicProfile, setPublicProfile] = useState(profile.publicProfile);
  const [fullNameVisible, setFullNameVisible] = useState(profile.privacySettings.fullName);
  const [checkInsVisible, setCheckInsVisible] = useState(profile.privacySettings.checkIns);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const updateProfile = trpc.user.updateProfile.useMutation({
    onSuccess: () => {
      utils.user.getMe.invalidate();
      setIsEditing(false);
      setSubmitError(null);
    },
    onError: (error) => {
      setSubmitError(error.message);
    },
  });

  const resetForm = () => {
    setName(profile.name || '');
    setBio(profile.bio || '');
    setBirthday(profile.birthday ? profile.birthday.split('T')[0] : '');
    setCity(profile.homeLocation?.city || '');
    setState(profile.homeLocation?.state || '');
    setCountry(profile.homeLocation?.country || '');
    setPublicProfile(profile.publicProfile);
    setFullNameVisible(profile.privacySettings.fullName);
    setCheckInsVisible(profile.privacySettings.checkIns);
    setSubmitError(null);
  };

  const handleCancel = () => {
    resetForm();
    setIsEditing(false);
  };

  const handleSave = () => {
    updateProfile.mutate({
      name: name || undefined,
      bio,
      birthday: birthday || undefined,
      homeLocation: { city, state, country },
      publicProfile,
      privacySettings: {
        fullName: fullNameVisible,
        checkIns: checkInsVisible,
      },
    });
  };

  const formatLocation = () => {
    const parts = [
      profile.homeLocation?.city,
      profile.homeLocation?.state,
      profile.homeLocation?.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const formatBirthday = () => {
    if (!profile.birthday) return null;
    return new Date(profile.birthday).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isEditing) {
    return (
      <div>
        {/* Action buttons at top right per CLAUDE.md */}
        <FormSection
          title="Edit Profile"
          contentClassName="space-y-5"
          actions={
            <>
              {submitError && (
                <p className="text-sm text-red-600 self-center mr-2">{submitError}</p>
              )}
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={updateProfile.isPending}
                loading={updateProfile.isPending}
              >
                Save
              </Button>
            </>
          }
        >
          <div>
            <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              id="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500"
              placeholder="Your display name"
            />
          </div>

          <div>
            <label htmlFor="profile-bio" className="block text-sm font-medium text-gray-700 mb-1">
              Bio
            </label>
            <textarea
              id="profile-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500"
              placeholder="Tell us about yourself and your wine journey..."
            />
            <p className="text-xs text-gray-400 mt-1">{bio.length}/500</p>
          </div>

          <div>
            <label
              htmlFor="profile-birthday"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Birthday
            </label>
            <input
              type="date"
              id="profile-birthday"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500"
            />
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-1">Home Location</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500"
                placeholder="City"
              />
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500"
                placeholder="State"
              />
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500"
                placeholder="Country"
              />
            </div>
          </fieldset>

          {/* Privacy section */}
          <FormSection title="Privacy" titleAs="h3" contentClassName="space-y-1">
              <Toggle
                label="Public Profile"
                description="Allow other users to find and view your profile"
                checked={publicProfile}
                onChange={setPublicProfile}
                disabled={updateProfile.isPending}
              />
              <Toggle
                label="Show Full Name"
                description="Display your full name on your public profile"
                checked={fullNameVisible}
                onChange={setFullNameVisible}
                disabled={updateProfile.isPending || !publicProfile}
              />
              <Toggle
                label="Show Check-ins"
                description="Display your check-in history on your public profile"
                checked={checkInsVisible}
                onChange={setCheckInsVisible}
                disabled={updateProfile.isPending || !publicProfile}
              />
            {!publicProfile && (
              <p className="text-xs text-gray-400 mt-2">
                Granular settings only apply when your profile is public.
              </p>
            )}
          </FormSection>
        </FormSection>
      </div>
    );
  }

  // View mode
  return (
    <div>
      <FormSection
        title="Profile Info"
        actions={
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            Edit Profile
          </Button>
        }
      >
        <dl className="space-y-2">
          <ProfileField label="Name" value={profile.name} />
          <ProfileField label="Email" value={email} />
          <ProfileField label="Bio" value={profile.bio} placeholder="No bio yet" />
          <ProfileField label="Birthday" value={formatBirthday()} />
          <ProfileField label="Location" value={formatLocation()} />
        </dl>
      </FormSection>

      {/* Privacy section (view mode) */}
      <FormSection title="Privacy" titleAs="h3">
        <dl className="space-y-2">
          <div className="flex items-start justify-between gap-4">
            <dt className="text-sm text-gray-500 flex-shrink-0">Public Profile</dt>
            <dd className="text-sm text-right">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  profile.publicProfile
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {profile.publicProfile ? 'Visible' : 'Hidden'}
              </span>
            </dd>
          </div>
          <div className="flex items-start justify-between gap-4">
            <dt className="text-sm text-gray-500 flex-shrink-0">Full Name Visible</dt>
            <dd className="text-sm text-gray-900 text-right">
              {profile.privacySettings.fullName ? 'Yes' : 'No'}
            </dd>
          </div>
          <div className="flex items-start justify-between gap-4">
            <dt className="text-sm text-gray-500 flex-shrink-0">Check-ins Visible</dt>
            <dd className="text-sm text-gray-900 text-right">
              {profile.privacySettings.checkIns ? 'Yes' : 'No'}
            </dd>
          </div>
        </dl>
      </FormSection>
    </div>
  );
};
